import { useState } from "react";
import { useNavigate } from "react-router";
import type { DayCell } from "@/domain/calendar/CalendarModel";
import { addDays, makeDateKey, monthEnd, parseDateKey, startOfWeek, todayKey } from "@/domain/dates";
import { ScenarioSwitch } from "../components/ScenarioSwitch";
import { Screen } from "../components/Screen";
import { useApp } from "../container";
import { monthTitle, weekdayNames } from "../format";
import { useActiveSubscription } from "../hooks/useActiveSubscription";
import { useQuery } from "../hooks/useQuery";
import { useLocale } from "../i18n/LocaleProvider";
import styles from "./CalendarScreen.module.css";

export function CalendarScreen() {
  const app = useApp();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const today = todayKey(app.clock);
  const [cursor, setCursor] = useState(() => {
    const { year, month } = parseDateKey(today);
    return { year, month };
  });
  const active = useActiveSubscription();
  const subscription = active.selected;
  const scenario = active.scenarios.find((s) => s.id === subscription?.scenarioId);

  const entries = useQuery(async () => {
    if (!subscription) return [];
    const first = makeDateKey(cursor.year, cursor.month, 1);
    const range = { from: startOfWeek(first), to: addDays(startOfWeek(monthEnd(first)), 6) };
    return (await app.store.entries.listInRange(range)).filter((e) => e.subscriptionId === subscription.id);
  }, [cursor.year, cursor.month, subscription?.id ?? ""], app.changeSources);

  const track = (name: string) => app.analytics.track(name);
  const shift = (delta: number) =>
    setCursor(({ year, month }) => {
      const next = new Date(Date.UTC(year, month - 1 + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1 };
    });

  const grid = app.calendar.monthGrid(cursor.year, cursor.month, today, entries.data ?? [], () => scenario);
  const hasMarks = grid.weeks.some((week) => week.some((cell) => cell.inMonth && cell.marks.some((m) => m.marked)));
  const tone = scenario?.calendarMarker?.tone ?? "accent";
  const dayLabel = (cell: DayCell) => `${parseDateKey(cell.date).day}, ${cell.date}`;

  return (
    <Screen title={t.calendar.title}>
      <ScenarioSwitch subscriptions={active.subscriptions} scenarios={active.scenarios} value={subscription?.id} onChange={(id) => { track("calendar_scenario_switch"); active.select(id); }} />
      {!active.loading && !subscription && <p className={styles.empty}>{t.calendar.noScenarios}</p>}
      <div className={styles.monthNav}>
        <button type="button" className={`${styles.navButton} ${styles.yearButton}`} onClick={() => { track("calendar_year_prev"); shift(-12); }} aria-label={t.calendar.previousYear}>« {cursor.year - 1}</button>
        <button type="button" className={styles.navButton} onClick={() => { track("calendar_month_prev"); shift(-1); }} aria-label={t.calendar.previousMonth}>‹</button>
        <span className={styles.monthTitle}>{monthTitle(cursor.year, cursor.month, locale)}</span>
        <button type="button" className={styles.navButton} onClick={() => { track("calendar_month_next"); shift(1); }} aria-label={t.calendar.nextMonth}>›</button>
        <button type="button" className={`${styles.navButton} ${styles.yearButton}`} onClick={() => { track("calendar_year_next"); shift(12); }} aria-label={t.calendar.nextYear}>{cursor.year + 1} »</button>
      </div>
      <div className={styles.weekdays}>
        {weekdayNames(locale).map((name) => <span key={name} className={styles.weekday}>{name}</span>)}
      </div>
      <div key={`${cursor.year}-${cursor.month}`} className={`${styles.month} step-in`}>
      {grid.weeks.map((week) => (
        <div key={week[0]?.date} className={styles.week}>
          {week.map((cell) => (
            <button
              key={cell.date}
              type="button"
              className={[styles.day, cell.inMonth ? "" : styles.outside, cell.isToday ? styles.today : ""].join(" ")}
              disabled={cell.isFuture}
              aria-label={dayLabel(cell)}
              onClick={() => { track("calendar_day_open"); navigate(`/day/${cell.date}`); }}
            >
              <span className={styles.number}>{parseDateKey(cell.date).day}</span>
              <span className={styles.marks}>
                {cell.marks
                  .filter((mark) => mark.marked || mark.status !== "skipped")
                  .map((mark) => (
                    <span
                      key={mark.subscriptionId}
                      data-testid="mark"
                      data-marked={mark.marked}
                      data-tone={mark.tone}
                      data-highlighted={mark.highlighted}
                      data-status={mark.status}
                      className={styles.dot}
                    />
                  ))}
              </span>
            </button>
          ))}
        </div>
      ))}
      </div>
      {subscription && (
        <div className={styles.legend} aria-hidden="true">
          {tone === "warning" ? (
            <>
              <span><i className={styles.dot} data-tone="warning" data-highlighted="true" /> {t.calendar.legendAttackMeds}</span>
              <span><i className={styles.dot} data-tone="warning" data-highlighted="false" /> {t.calendar.legendAttack}</span>
              <span><i className={styles.dot} data-tone="warning" data-marked="false" /> {t.calendar.legendClear}</span>
            </>
          ) : (
            <span><i className={styles.dot} data-tone="accent" /> {t.calendar.legendRecorded}</span>
          )}
        </div>
      )}
      {entries.data && subscription && !hasMarks && <p className={styles.empty}>{t.calendar.noMarks}</p>}
    </Screen>
  );
}
